<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import ProductCard from '../components/ProductCard.vue'
import { useCartStore } from '../stores/cartStore'
import { useSettingsStore } from '../stores/settingsStore'
import { useWishlistStore } from '../stores/wishlistStore'
import { useCompareStore } from '../stores/compareStore'
import { useAuthStore } from '../stores/authStore'
import { useTrackingStore } from '../store/tracking'
import { useToast } from '../composables/useToast'

const route = useRoute()
const router = useRouter()
const cartStore = useCartStore()
const settingsStore = useSettingsStore()
const wishlistStore = useWishlistStore()
const compareStore = useCompareStore()
const authStore = useAuthStore()
const trackingStore = useTrackingStore()
const { showToast } = useToast()

const addToCart = async (product) => {
  try {
    await cartStore.addToCart(product, 1);
    showToast(`เพิ่ม ${product.title} ลงในตะกร้าแล้ว`, 'success');
    trackingStore.trackEvent({
      type: 'add_to_cart',
      productId: product.id,
      productName: product.title,
      price: product.price,
      quantity: 1,
      source: 'products_grid'
    });
  } catch (err) {
    showToast('ไม่สามารถเพิ่มลงตะกร้าได้', 'error');
  }
}

const toggleWishlistItem = async (e, product) => {
  e.preventDefault();
  e.stopPropagation();
  if (!authStore.isAuthenticated) {
    showToast('กรุณาเข้าสู่ระบบก่อนเพิ่มรายการโปรด', 'warning');
    return;
  }
  const result = await wishlistStore.toggleWishlist(product);
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการโปรดแล้ว' : 'นำออกจากรายการโปรดแล้ว', result.isAdded ? 'success' : 'info');
    trackingStore.trackEvent({
      type: result.isAdded ? 'add_to_wishlist' : 'remove_from_wishlist',
      productId: product.id,
      productName: product.title,
      source: 'products_grid'
    });
  }
}

const toggleCompareItem = (e, product) => {
  e.preventDefault();
  e.stopPropagation();
  const result = compareStore.toggleCompare(product);
  if (result.success) {
    showToast(result.isAdded ? 'เพิ่มในรายการเปรียบเทียบแล้ว' : 'นำออกจากรายการเปรียบเทียบแล้ว', result.isAdded ? 'success' : 'info');
  } else if (result.error === 'max_reached') {
    showToast('เปรียบเทียบได้สูงสุด 4 ชิ้น', 'warning');
  }
}

const allProducts = ref([])
const activeCategory = ref('ทุกหมวดหมู่')
const categories = ref(['ทุกหมวดหมู่'])
const categoryDetails = ref([])


const activeCategoryData = computed(() => {
  if (activeCategory.value === 'ทุกหมวดหมู่') return null;
  return categoryDetails.value.find(c => c.name === activeCategory.value) || null;
})

const searchQuery = ref(route.query.search || '')
const loading = ref(true)

let searchTimeout = null
watch(searchQuery, (newVal) => {
  if (searchTimeout) clearTimeout(searchTimeout)
  if (newVal.trim().length >= 2) {
    searchTimeout = setTimeout(() => {
      trackingStore.trackEvent({
        type: 'search_query',
        keyword: newVal.trim()
      })
    }, 1500)
  }
})

// Product list display
const visibleCount = ref(999999)
const loadMoreTrigger = ref(null)
let observer = null

const filteredProducts = computed(() => {
  let result = allProducts.value

  if (activeCategory.value !== 'ทุกหมวดหมู่') {
    result = result.filter(p => {
      if (p.categories && Array.isArray(p.categories)) {
        return p.categories.includes(activeCategory.value)
      }
      return p.category === activeCategory.value
    })
  }

  if (searchQuery.value.trim() !== '') {
    const q = searchQuery.value.toLowerCase()
    result = result.filter(p => p.title.toLowerCase().includes(q) || (p.category && p.category.toLowerCase().includes(q)))
  }

  // Sort by category order then product order
  result.sort((a, b) => {
    if (a.category_sort_order !== b.category_sort_order) {
      return a.category_sort_order - b.category_sort_order
    }
    return a.sort_order - b.sort_order
  })

  return result
})

const displayedProducts = computed(() => {
  return filteredProducts.value
})

const groupedDisplayedProducts = computed(() => {
  if (activeCategory.value !== 'ทุกหมวดหมู่') {
    return [{
      category: activeCategory.value,
      products: displayedProducts.value
    }]
  }

  const groups = []
  const catNames = categories.value.filter(c => c !== 'ทุกหมวดหมู่')

  catNames.forEach(catName => {
    const productsInCat = displayedProducts.value.filter(p => {
      if (Array.isArray(p.categories)) {
        return p.categories.includes(catName)
      }
      return p.category === catName
    })

    if (productsInCat.length > 0) {
      groups.push({
        category: catName,
        products: productsInCat
      })
    }
  })

  return groups
})

const setupObserver = () => {
  if (observer) observer.disconnect()
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && visibleCount.value < filteredProducts.value.length) {
        visibleCount.value += 24
      }
    })
  }, { rootMargin: '400px' })

  // Observe immediately if already mounted
  if (loadMoreTrigger.value) {
    observer.observe(loadMoreTrigger.value)
  }
}

watch(loadMoreTrigger, (el) => {
  if (el && observer) {
    observer.observe(el)
  }
})

watch(visibleCount, async () => {
  if (observer && loadMoreTrigger.value) {
    observer.unobserve(loadMoreTrigger.value)
    await nextTick()
    if (loadMoreTrigger.value) {
      observer.observe(loadMoreTrigger.value)
    }
  }
})

const setCategory = (cat) => {
  activeCategory.value = cat
  visibleCount.value = 24

  // Update URL without page reload
  if (cat === 'ทุกหมวดหมู่') {
    router.push('/products')
  } else {
    router.push(`/products/category/${encodeURIComponent(cat)}`)
  }
}

const formatPrice = (price) => {
  if (!price) return '0'
  return new Intl.NumberFormat('th-TH').format(price)
}

const now = ref(Date.now())
let timerInterval = null

const getTimeRemaining = (endTime) => {
  if (!endTime) return null
  const total = Date.parse(endTime) - now.value
  if (total <= 0) return null
  const days = Math.floor(total / (1000 * 60 * 60 * 24))
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24)
  const minutes = Math.floor((total / 1000 / 60) % 60)
  
  if (days > 0) return `จบโปรใน ${days} วัน ${hours} ชม.`
  if (hours > 0) return `จบโปรใน ${hours} ชม. ${minutes} นาที`
  return `จบโปรใน ${minutes} นาที`
}

const fetchCategories = async () => {
  try {
    const res = await fetch('/api/categories')
    const data = await res.json()
    if (data.success) {
      categories.value = ['ทุกหมวดหมู่', ...data.data.map(c => c.name)]
      categoryDetails.value = data.data
    }
  } catch (error) {
    console.error('Failed to fetch categories:', error)
  }
}

const fetchProducts = async () => {
  try {
    const res = await fetch('/api/products')
    const data = await res.json()
    if (data.success) {
      allProducts.value = data.data.map(p => {
        const catDetails = categoryDetails.value.find(c => c.name === p.category);
        const catSortOrder = catDetails ? (catDetails.sort_order || 0) : 9999;
        
        let parsedCategories = []
        if (typeof p.categories === 'string') {
          try { parsedCategories = JSON.parse(p.categories) || [] } catch (e) { parsedCategories = [] }
        } else if (Array.isArray(p.categories)) {
          parsedCategories = p.categories
        }
        if (parsedCategories.length === 0 && p.category) {
          parsedCategories = [p.category]
        }

        return {
          id: p.id,
          sku: p.sku || '',
          slug: p.slug,
          category: p.category || 'ไม่มีหมวดหมู่',
          categories: parsedCategories,
          title: p.name,
          image: p.image_url || 'https://images.unsplash.com/photo-1542838132-92c53300491e?w=800&q=80',
          price: p.price,
          original_price: p.original_price,
          rating: Number(p.rating || 5).toFixed(1),
          reviews: p.review_count || 0,
          is_out_of_stock: p.is_out_of_stock || false,
          sale_end_date: p.sale_end_date,
          stock_quantity: p.stock_quantity,
          card_features: p.card_features || null,
          compare_enabled: p.compare_enabled !== false,
          sort_order: p.sort_order || 0,
          category_sort_order: catSortOrder
        }
      })
    }
  } catch (error) {
    console.error('Failed to fetch products:', error)
  } finally {
    loading.value = false
  }
}

const applyCategoryFromRoute = () => {
  // Support both /products/category/:name and /products?category=name
  const cat = route.params.category 
    ? decodeURIComponent(route.params.category) 
    : (route.query.category ? decodeURIComponent(route.query.category) : null)
  if (cat && categories.value.includes(cat)) {
    activeCategory.value = cat
    document.title = settingsStore.storeName ? `สินค้าหมวดหมู่ ${cat} | ${settingsStore.storeName}` : `สินค้าหมวดหมู่ ${cat}`
  } else if (cat) {
    // Category from URL not found yet, try matching
    activeCategory.value = cat
    document.title = settingsStore.storeName ? `สินค้าหมวดหมู่ ${cat} | ${settingsStore.storeName}` : `สินค้าหมวดหมู่ ${cat}`
  } else {
    activeCategory.value = 'ทุกหมวดหมู่'
  }
  
  if (route.query.search !== undefined) {
    searchQuery.value = route.query.search
  }
  
  visibleCount.value = 24
}

// Watch route changes (both params and query)
watch([() => route.params.category, () => route.query.category, () => route.query.search], () => {
  applyCategoryFromRoute()
})

onMounted(async () => {
  await fetchCategories()
  await fetchProducts()
  applyCategoryFromRoute()
  if (authStore.isAuthenticated) {
    await wishlistStore.fetchWishlist()
  }
  setupObserver()
  timerInterval = setInterval(() => { now.value = Date.now() }, 60000)
  
})

onUnmounted(() => {
  if (timerInterval) clearInterval(timerInterval)
  if (observer) observer.disconnect()
})

</script>

<template>
  <div class="bg-[#f8f9fa] dark:bg-[#0a0f16] min-h-screen pt-24 md:pt-32 pb-10 md:pb-24 transition-colors">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <!-- Premium Header Section -->
      <div v-if="!activeCategoryData" class="relative text-center max-w-5xl mx-auto mb-16 px-4 pt-8">
        <!-- Decorative Background Elements -->
        <div class="absolute inset-0 pointer-events-none overflow-hidden -z-10">
          <!-- Central Glows -->
          <div class="absolute top-[10%] left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-r from-emerald-400/20 to-teal-400/25 rounded-full blur-[100px] opacity-70"></div>
          
          <!-- Top Right floating sphere -->
          <div class="absolute top-[5%] right-[5%] w-20 h-20 rounded-full bg-gradient-to-br from-emerald-300 via-emerald-500 to-emerald-800 shadow-[inset_-6px_-6px_20px_rgba(0,0,0,0.3),_0_15px_30px_rgba(16,185,129,0.2)] opacity-85 hover:scale-110 transition-transform duration-700 ease-out hidden md:block"></div>
          
          <!-- Top Right Dot Grid -->
          <div class="absolute top-[8%] right-[2%] opacity-[0.15] dark:opacity-[0.05] hidden md:block">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="currentColor" class="text-gray-900 dark:text-white">
              <circle cx="5" cy="5" r="2" />
              <circle cx="20" cy="5" r="2" />
              <circle cx="35" cy="5" r="2" />
              <circle cx="50" cy="5" r="2" />
              <circle cx="5" cy="20" r="2" />
              <circle cx="20" cy="20" r="2" />
              <circle cx="35" cy="20" r="2" />
              <circle cx="50" cy="20" r="2" />
              <circle cx="5" cy="35" r="2" />
              <circle cx="20" cy="35" r="2" />
              <circle cx="35" cy="35" r="2" />
              <circle cx="50" cy="35" r="2" />
              <circle cx="5" cy="50" r="2" />
              <circle cx="20" cy="50" r="2" />
              <circle cx="35" cy="50" r="2" />
              <circle cx="50" cy="50" r="2" />
            </svg>
          </div>

          <!-- Middle Left Green Sphere (Blurred) -->
          <div class="absolute top-[40%] left-[2%] w-24 h-24 rounded-full bg-gradient-to-br from-emerald-400 via-emerald-500 to-emerald-700 shadow-[inset_-8px_-8px_25px_rgba(0,0,0,0.25),_0_20px_35px_rgba(16,185,129,0.15)] filter blur-[1px] opacity-75 hidden md:block"></div>

          <!-- Bottom Left White/Glass Sphere -->
          <div class="absolute bottom-[5%] left-[8%] w-16 h-16 rounded-full bg-white/40 dark:bg-white/5 border border-white/50 dark:border-white/10 shadow-[inset_-4px_-4px_12px_rgba(0,0,0,0.05),_inset_4px_4px_12px_rgba(255,255,255,0.4),_0_15px_30px_rgba(0,0,0,0.05)] backdrop-blur-[1px] opacity-80 hidden md:block"></div>

          <!-- Bottom Left Dot Grid -->
          <div class="absolute bottom-[8%] left-[2%] opacity-[0.15] dark:opacity-[0.05] hidden md:block">
            <svg width="60" height="60" viewBox="0 0 60 60" fill="currentColor" class="text-gray-900 dark:text-white">
              <circle cx="5" cy="5" r="2" />
              <circle cx="20" cy="5" r="2" />
              <circle cx="35" cy="5" r="2" />
              <circle cx="5" cy="20" r="2" />
              <circle cx="20" cy="20" r="2" />
              <circle cx="35" cy="20" r="2" />
              <circle cx="5" cy="35" r="2" />
              <circle cx="20" cy="35" r="2" />
              <circle cx="35" cy="35" r="2" />
            </svg>
          </div>

          <!-- Bottom Right White Sphere (Foreground blur) -->
          <div class="absolute bottom-[10%] right-[15%] w-28 h-28 rounded-full bg-white/30 dark:bg-white/5 border border-white/40 dark:border-white/10 shadow-[inset_-8px_-8px_25px_rgba(0,0,0,0.05),_inset_8px_8px_25px_rgba(255,255,255,0.3),_0_25px_45px_rgba(0,0,0,0.05)] backdrop-blur-[2px] filter blur-[2px] opacity-70 hidden md:block"></div>

          <!-- Bottom Right Leaf/Lens-flare shape -->
          <div class="absolute bottom-[-5%] right-[2%] w-56 h-56 rounded-tr-[10rem] rounded-bl-[10rem] bg-gradient-to-tr from-emerald-400/20 to-teal-300/10 backdrop-blur-[3px] border-r border-t border-white/30 rotate-12 opacity-80 hidden md:block"></div>
        </div>

        <div class="relative z-10 flex flex-col items-center">
          <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50/60 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 text-emerald-800 dark:text-emerald-400 text-sm font-semibold mb-6 backdrop-blur-sm shadow-sm hover:scale-105 transition-transform cursor-default">
              <span class="relative flex h-2 w-2">
                <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span class="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              {{ settingsStore.productsHeaderBadge || 'พร้อมจัดส่งทั่วประเทศ' }}
          </div>

          <h1 class="text-4xl md:text-5xl lg:text-[4.5rem] font-black tracking-tight text-gray-900 dark:text-white mb-6 leading-[1.15] flex flex-col items-center">
            <span class="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 dark:text-gray-100 mb-2">{{ settingsStore.productsHeaderTitleMain || 'แคตตาล็อกสินค้ากว่า' }}</span>
            <span class="inline-flex items-center gap-3 md:gap-4 mt-2">
              <span class="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 via-emerald-600 to-teal-500 dark:from-emerald-400 dark:to-teal-300 filter drop-shadow-[0_4px_6px_rgba(16,185,129,0.1)]">{{ settingsStore.productsHeaderTitleNumber || '100' }}</span>
              <span class="text-emerald-600 dark:text-emerald-400 font-bold">{{ settingsStore.productsHeaderTitleSuffix || 'แบบ' }}</span>
              <svg class="w-8 h-8 md:w-10 md:h-10 text-emerald-500 animate-pulse flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2l2.4 7.2 7.2 2.4-7.2 2.4-2.4 7.2-2.4-7.2-7.2-2.4 7.2-2.4z"/>
              </svg>
            </span>
          </h1>
          <p class="text-gray-500 dark:text-gray-400 text-base md:text-lg font-light leading-relaxed max-w-2xl mx-auto mb-10 whitespace-pre-line">
            {{ settingsStore.productsHeaderDescription || 'ค้นพบพื้นที่เก็บของ โกดัง และโรงเรือนทุกขนาด ที่ออกแบบมาเพื่อยกระดับพื้นที่ของคุณ\nโดยเฉพาะ สวยงาม ทนแดด ทนฝน ด้วยวัสดุคุณภาพในดีไซน์ที่ลงตัว' }}
          </p>
        </div>
      </div>

      <!-- Category Hero Section (Dynamic) -->
      <div v-else class="mb-16 rounded-[2.5rem] overflow-hidden bg-white dark:bg-[#111827] border border-gray-100 dark:border-white/5 shadow-2xl shadow-gray-200/50 dark:shadow-none relative flex flex-col md:flex-row">
        <!-- Content Side -->
        <div class="w-full md:w-1/2 p-8 md:p-12 lg:p-16 flex flex-col justify-center relative z-10 order-2 md:order-1">
          <!-- Decorative Background Blur -->
          <div class="absolute -top-24 -left-24 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"></div>
          
          <div class="flex flex-col sm:flex-row sm:items-center gap-4 mb-6 relative">
             <div class="w-16 h-16 rounded-2xl bg-emerald-50 dark:bg-[#1a2333] flex items-center justify-center border border-emerald-100 dark:border-white/10 shadow-sm flex-shrink-0">
               <img v-if="activeCategoryData.icon_url" :src="activeCategoryData.icon_url" :alt="activeCategoryData.name" class="w-10 h-10 object-contain opacity-70 dark:opacity-90">
               <svg v-else class="w-8 h-8 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
             </div>
             <div class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-100 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold tracking-widest uppercase self-start sm:self-auto">
               <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"></path></svg>
               หมวดหมู่ที่เลือก
             </div>
          </div>
          
          <h1 class="text-3xl md:text-4xl lg:text-5xl font-black tracking-tight text-gray-900 dark:text-white mb-6 leading-tight relative">
            {{ activeCategoryData.name }}
          </h1>
          
          <p class="text-base md:text-lg text-gray-600 dark:text-gray-400 font-light leading-relaxed mb-8 max-w-lg relative">
            {{ activeCategoryData.description || 'ค้นหาพื้นที่เก็บของในสไตล์คุณ การันตีคุณภาพและความสวยงามระดับพรีเมียม ตอบสนองทุกฟังก์ชันการใช้งาน' }}
          </p>
          
          <div class="flex items-center gap-4 relative mt-auto">
             <div class="flex -space-x-3">
                <img src="https://i.pravatar.cc/100?img=33" class="w-10 h-10 rounded-full border-2 border-white dark:border-[#111827]" alt="user">
                <img src="https://i.pravatar.cc/100?img=47" class="w-10 h-10 rounded-full border-2 border-white dark:border-[#111827]" alt="user">
                <img src="https://i.pravatar.cc/100?img=12" class="w-10 h-10 rounded-full border-2 border-white dark:border-[#111827]" alt="user">
                <div class="w-10 h-10 rounded-full border-2 border-white dark:border-[#111827] bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-xs font-bold text-gray-600 dark:text-gray-300 shadow-sm">+10k</div>
             </div>
             <span class="text-sm text-gray-500 dark:text-gray-400 font-medium hidden sm:inline-block">ผู้ใช้งานที่ไว้วางใจทั่วประเทศ</span>
             <span class="text-sm text-gray-500 dark:text-gray-400 font-medium sm:hidden">ลูกค้าที่ไว้วางใจ</span>
          </div>
        </div>

        <!-- Image Side -->
        <div class="w-full md:w-1/2 min-h-[250px] md:min-h-0 relative bg-gray-100 dark:bg-gray-800 order-1 md:order-2">
          <img v-if="activeCategoryData.image_url" :src="activeCategoryData.image_url" :alt="activeCategoryData.name" class="absolute inset-0 w-full h-full object-cover">
          <div v-else class="absolute inset-0 w-full h-full bg-gradient-to-tr from-emerald-100 to-teal-50 dark:from-gray-800 dark:to-gray-700 flex items-center justify-center">
             <svg class="w-24 h-24 text-emerald-200 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
          </div>
          
          <!-- Gradient Overlay for smooth transition to content -->
          <div class="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent dark:from-[#111827] dark:via-[#111827]/40 md:bg-gradient-to-l md:from-transparent md:via-white/40 md:to-white md:dark:via-[#111827]/40 md:dark:to-[#111827] pointer-events-none transition-all duration-300"></div>
        </div>
      </div>

      <!-- Category Filter -->
      <div class="relative w-full max-w-6xl mx-auto mb-16 px-4">
        <!-- Wrapped flex container containing category cards -->
        <div class="flex flex-wrap items-center justify-center gap-3 w-full">
          <template v-for="cat in categories" :key="cat">
            <button 
              @click="setCategory(cat)"
              :class="[
                'flex-shrink-0 group flex items-center gap-2.5 px-5 py-2.5 rounded-2xl text-xs sm:text-sm font-bold transition-all duration-300 whitespace-nowrap border',
                activeCategory === cat 
                  ? 'bg-[#0b2b24] dark:bg-emerald-600 text-white border-[#0b2b24] dark:border-emerald-600 shadow-[0_8px_20px_rgba(11,43,36,0.15)] dark:shadow-[0_8px_20px_rgba(16,185,129,0.25)] hover:scale-102' 
                  : 'bg-white dark:bg-[#1a2333] text-gray-700 dark:text-gray-300 border-gray-200/80 dark:border-white/5 hover:bg-gray-50 dark:hover:bg-[#222e44] hover:border-gray-300 dark:hover:border-white/10 hover:shadow-md hover:-translate-y-0.5'
              ]"
            >
              <!-- Icon -->
              <span class="flex-shrink-0 flex items-center justify-center">
                <svg v-if="cat === 'ทุกหมวดหมู่'" class="w-4.5 h-4.5 transition-transform group-hover:scale-110 duration-300" :class="activeCategory === cat ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>
                </svg>
                <svg v-else-if="cat.includes('โรงเรือน')" class="w-4.5 h-4.5 transition-transform group-hover:scale-110 duration-300" :class="activeCategory === cat ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
                </svg>
                <svg v-else-if="cat.includes('โกดัง') || cat.includes('เก็บของ')" class="w-4.5 h-4.5 transition-transform group-hover:scale-110 duration-300" :class="activeCategory === cat ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
                <svg v-else class="w-4.5 h-4.5 transition-transform group-hover:scale-110 duration-300" :class="activeCategory === cat ? 'text-white' : 'text-emerald-600 dark:text-emerald-400'" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                </svg>
              </span>

              <!-- Name -->
              <span class="tracking-wide">{{ cat }}</span>

              <!-- Count Badge -->
              <span 
                v-if="cat !== 'ทุกหมวดหมู่'" 
                class="flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-black rounded-full transition-all duration-300" 
                :class="activeCategory === cat 
                  ? 'bg-white/20 text-white' 
                  : 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 border border-emerald-150 dark:border-emerald-500/15'"
              >
                {{ allProducts.filter(p => p.categories ? p.categories.includes(cat) : p.category === cat).length }}
              </span>
            </button>
          </template>
        </div>
      </div>

      <!-- Loading Skeleton -->
      <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
        <div v-for="i in 8" :key="'skeleton-' + i" class="bg-white dark:bg-[#111827] rounded-3xl overflow-hidden border border-gray-100 dark:border-gray-800 flex flex-col h-full animate-pulse">
          <div class="aspect-square bg-gray-200 dark:bg-gray-800"></div>
          <div class="p-6 flex flex-col flex-grow">
            <div class="h-5 bg-gray-200 dark:bg-gray-800 rounded-md w-3/4 mb-3"></div>
            <div class="h-3 bg-gray-200 dark:bg-gray-800 rounded-md w-1/2 mb-6"></div>
            <div class="mt-auto flex justify-between items-end pt-4 border-t border-gray-50 dark:border-gray-800">
              <div>
                <div class="h-3 bg-gray-200 dark:bg-gray-800 rounded-md w-12 mb-2"></div>
                <div class="h-6 bg-gray-200 dark:bg-gray-800 rounded-md w-24"></div>
              </div>
              <div class="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-800"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- State: No Results -->
      <div v-else-if="filteredProducts.length === 0" class="py-20 text-center">
         <div class="mb-4 text-gray-300 dark:text-gray-700"><svg class="w-16 h-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg></div>
         <h3 class="text-xl font-bold text-gray-900 dark:text-white mb-2">ไม่พบสินค้าที่คุณค้นหา</h3>
         <p class="text-gray-500 dark:text-gray-400 mb-6">ลองเปลี่ยนคำค้นหาหรือเลือกหมวดหมู่ใหม่อีกครั้ง</p>
         <button @click="setCategory('ทุกหมวดหมู่')" class="text-emerald-600 dark:text-emerald-500 font-bold hover:underline">ดูสินค้าทั้งหมด</button>
      </div>

      <!-- Grouped Products -->
      <div v-else class="space-y-16">
        <div v-for="group in groupedDisplayedProducts" :key="group.category" class="space-y-6">
          <!-- Category Header -->
          <div v-if="activeCategory === 'ทุกหมวดหมู่'" class="flex items-center gap-4 pb-4 border-b border-gray-100 dark:border-gray-800">
             <h2 class="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">{{ group.category }}</h2>
             <span class="px-3 py-1 text-sm font-medium bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400 rounded-full">
              {{ allProducts.filter(p => p.categories ? p.categories.includes(group.category) : p.category === group.category).length }} รายการ
             </span>
          </div>

          <!-- Products Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
            <ProductCard 
              v-for="product in group.products" 
              :key="product.id"
              :product="product"
            />
          </div>
        </div>
      </div>

      <!-- Infinite Scroll Trigger -->
      <div v-if="visibleCount < filteredProducts.length" ref="loadMoreTrigger" class="h-20 flex flex-col items-center justify-center mt-12 gap-3 opacity-70">
        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
        <span class="text-sm text-gray-500 font-medium">กำลังโหลดเพิ่ม...</span>
      </div>
      <div v-else-if="filteredProducts.length > 0" class="mt-16 text-center pb-8">
        <div class="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-full text-sm text-gray-400 dark:text-gray-500 border border-gray-100 dark:border-gray-800">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
          แสดงสินค้าครบทั้งหมด {{ filteredProducts.length }} รายการ
        </div>
      </div>

    </div>
  </div>
</template>

<style scoped>
/* Hide scrollbar for horizontal scroll on mobile */
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}
.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>
